import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Plus, Pencil, Trash2, Pill, ShieldCheck, Package, FileText, Scissors,
  ClipboardList, RotateCw, RefreshCw, Building2, Sparkles, Loader2, Download,
  FlaskConical, Activity, Stethoscope,
} from "lucide-react";
import { toast } from "sonner";

// 6 master kinds — labels & icons
const KINDS = [
  { key: "medicines",        label: "Pharmacy Items",       icon: Pill,         color: "text-amber-600",  bg: "bg-amber-50" },
  { key: "panel_companies",  label: "TPA / Panel Companies", icon: ShieldCheck,  color: "text-leaf-700",   bg: "bg-leaf/10" },
  { key: "implants",         label: "Implants",              icon: Package,      color: "text-pink-600",   bg: "bg-pink-50" },
  { key: "opd_templates",    label: "OPD Templates",         icon: FileText,     color: "text-sky-600",    bg: "bg-sky-50" },
  { key: "ot_templates",     label: "OT Note Templates",     icon: Scissors,     color: "text-rose-600",   bg: "bg-rose-50" },
  { key: "discharge_templates", label: "Discharge Templates", icon: ClipboardList, color: "text-indigo-600", bg: "bg-indigo-50" },
  { key: "investigations",   label: "Investigations Advised", icon: FlaskConical, color: "text-violet-600", bg: "bg-violet-50" },
  { key: "procedures",       label: "Procedures Advised",     icon: Activity,     color: "text-cyan-600",   bg: "bg-cyan-50" },
  { key: "surgeries",        label: "Surgeries Advised",      icon: Stethoscope,  color: "text-rose-600",   bg: "bg-rose-50" },
];

// Field schemas per kind — control what shows in the "Add/Edit" dialog
const SCHEMA = {
  medicines: [
    { key: "generic",  label: "Generic / Salt" },
    { key: "strength", label: "Strength (e.g. 0.5%, 10mg)" },
    { key: "form",     label: "Form (Eye drop, Tab, Syrup)" },
    { key: "unit",     label: "Unit (ml, tab, vial)" },
    { key: "mrp",      label: "MRP (₹)", type: "number" },
    { key: "hsn",      label: "HSN code" },
  ],
  panel_companies: [
    { key: "type",      label: "Type (Insurance / TPA / Govt scheme)" },
    { key: "contact",   label: "Contact person" },
    { key: "phone",     label: "Phone" },
    { key: "email",     label: "Email" },
    { key: "address",   label: "Address" },
    { key: "discount_percent", label: "Default discount (%)", type: "number" },
  ],
  implants: [
    { key: "type",      label: "Type (IOL / Stent / etc.)" },
    { key: "brand",     label: "Brand / Manufacturer" },
    { key: "model",     label: "Model" },
    { key: "size",      label: "Size / Specification" },
    { key: "mrp",       label: "MRP (₹)", type: "number" },
    { key: "hsn",       label: "HSN code" },
  ],
  opd_templates: [
    { key: "category",  label: "Category (Cataract, DR, Glaucoma...)" },
    { key: "diagnosis", label: "Diagnosis", type: "textarea" },
    { key: "icd_code",  label: "ICD-10 code" },
    { key: "treatment_advice", label: "Treatment advice", type: "textarea" },
  ],
  ot_templates: [
    { key: "category",         label: "Category" },
    { key: "procedure_name",   label: "Procedure name" },
    { key: "anaesthesia",      label: "Anaesthesia" },
    { key: "procedure_details", label: "Procedure details", type: "textarea" },
  ],
  discharge_templates: [
    { key: "category",  label: "Category" },
    { key: "description", label: "Description", type: "textarea" },
  ],
  investigations: [
    { key: "category",       label: "Category (Imaging, Lab, Refraction, Glaucoma...)" },
    { key: "code",           label: "Code (CPT / local)" },
    { key: "estimated_cost", label: "Estimated cost (₹)", type: "number" },
    { key: "notes",          label: "Notes", type: "textarea" },
  ],
  procedures: [
    { key: "category",       label: "Category (Office, Laser, Lacrimal...)" },
    { key: "code",           label: "Code" },
    { key: "duration_min",   label: "Duration (min)", type: "number" },
    { key: "estimated_cost", label: "Estimated cost (₹)", type: "number" },
    { key: "notes",          label: "Notes", type: "textarea" },
  ],
  surgeries: [
    { key: "category",       label: "Category (Cataract, Glaucoma, Cornea, VR...)" },
    { key: "code",           label: "Code" },
    { key: "duration_min",   label: "Duration (min)", type: "number" },
    { key: "estimated_cost", label: "Estimated cost (₹)", type: "number" },
    { key: "notes",          label: "Notes", type: "textarea" },
  ],
};

const EMPTY = { name: "", master_seed_key: "", fields: {} };

export default function SuperAdminMasters() {
  const [active, setActive] = useState("medicines");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [loadingPack, setLoadingPack] = useState(false);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const schema = SCHEMA[active] || [];
  const Tab = KINDS.find((k) => k.key === active);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/superadmin/masters/${active}`);
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const loadPreview = async () => {
    try {
      const { data } = await api.get(`/superadmin/master-sync/preview`);
      setPreview(data);
    } catch { /* non-blocking */ }
  };

  useEffect(() => { load(); }, [active]);
  useEffect(() => { loadPreview(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", master_seed_key: "", fields: {} });
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    // Re-derive fields from the row (whitelisted by schema)
    const fields = {};
    schema.forEach((f) => { if (it[f.key] !== undefined) fields[f.key] = it[f.key]; });
    // Also include any non-schema fields that templates carry (e.g. "template" blob)
    setForm({ name: it.name || "", master_seed_key: it.master_seed_key || "", fields });
    setOpen(true);
  };
  const setField = (k, v) => setForm((s) => ({ ...s, fields: { ...s.fields, [k]: v } }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editing) {
        await api.patch(`/superadmin/masters/${active}/${editing.id}`, { name: form.name, fields: form.fields });
        toast.success("Master updated");
      } else {
        await api.post(`/superadmin/masters/${active}`, form);
        toast.success("Master added");
      }
      setOpen(false); load(); loadPreview();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete master "${it.name}"? Hospitals already seeded keep their copy.`)) return;
    try {
      await api.delete(`/superadmin/masters/${active}/${it.id}`);
      toast.success("Master deleted");
      load(); loadPreview();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const syncAll = async () => {
    if (!window.confirm("Push all NEW master items to ALL active hospitals? Existing copies are never overwritten.")) return;
    setSyncing(true);
    try {
      const { data } = await api.post(`/superadmin/master-sync`, {});
      const t = data.summary.totals;
      const total = Object.values(t).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Synced — ${total} item(s) pushed across ${data.summary.hospitals.length} hospital(s).`);
      loadPreview();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  const loadStarterPack = async () => {
    if (!window.confirm("Load the bundled starter pack (50+ eye-care drugs, 20 Indian TPAs, 25 IOL/implant items, 18 OPD/OT/Discharge templates) into the master library? Items already present are skipped.")) return;
    setLoadingPack(true);
    try {
      const { data } = await api.post(`/superadmin/master-sync/load-starter-pack`, {});
      const t = data.total || 0;
      toast.success(`Starter pack loaded — ${t} new master item(s).`);
      load(); loadPreview();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setLoadingPack(false); }
  };

  const pendingTotal = useMemo(() => {
    if (!preview) return 0;
    return preview.hospitals.reduce((sum, h) => sum + Object.values(h.pending).reduce((a, b) => a + (b || 0), 0), 0);
  }, [preview]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="section-label">SaaS Console · Master Library</div>
          <h1 className="font-heading text-3xl font-semibold">Global Master Catalogue</h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-muted">
            Items added here auto-seed into every <b>new</b> hospital at signup. For existing hospitals, click <b>Sync to all</b> to push new items.
            Hospitals can edit their own copies freely — those edits never affect the master or other hospitals.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {preview && pendingTotal > 0 && (
            <Badge className="border-amber-300 bg-amber-50 text-amber-800" variant="outline">
              {pendingTotal} item{pendingTotal > 1 ? "s" : ""} pending sync
            </Badge>
          )}
          <Button
            variant="outline"
            onClick={loadStarterPack}
            disabled={loadingPack}
            data-testid="masters-load-starter-pack"
            className="border-amber-300 text-amber-800 hover:bg-amber-50"
          >
            {loadingPack ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Download size={14} className="mr-2" />}
            Load starter pack
          </Button>
          <Button
            variant="outline"
            onClick={syncAll}
            disabled={syncing}
            data-testid="masters-sync-all"
            className="border-brand/40 text-brand hover:bg-brand/5"
          >
            {syncing ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />}
            Sync to all hospitals
          </Button>
        </div>
      </div>

      {/* Stats per kind */}
      {preview && (
        <Card className="grid gap-3 p-4 sm:grid-cols-3 lg:grid-cols-6" data-testid="masters-counts">
          {KINDS.map((k) => {
            const Icon = k.icon;
            return (
              <div key={k.key} className={`rounded-lg ${k.bg} p-3`}>
                <div className="flex items-center gap-2">
                  <Icon size={14} className={k.color} />
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">{k.label}</span>
                </div>
                <div className="mt-1 text-2xl font-semibold tabular-nums">{preview.master_counts[k.key] || 0}</div>
                <div className="text-[10px] text-ink-muted">in master library</div>
              </div>
            );
          })}
        </Card>
      )}

      <Tabs value={active} onValueChange={setActive}>
        <TabsList className="w-full justify-start overflow-x-auto" data-testid="masters-tabs">
          {KINDS.map((k) => (
            <TabsTrigger key={k.key} value={k.key} data-testid={`masters-tab-${k.key}`}>
              <k.icon size={13} className={`mr-2 ${k.color}`} /> {k.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {KINDS.map((k) => (
          <TabsContent key={k.key} value={k.key} className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-ink-muted">
                <b className="text-ink">{items.length}</b> master {k.label.toLowerCase()} — added here will seed into every new hospital automatically.
              </div>
              <Button onClick={openCreate} className="bg-brand hover:bg-brand-700" data-testid={`masters-add-${k.key}`}>
                <Plus size={14} className="mr-2" /> Add to master
              </Button>
            </div>

            <Card className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/60 text-left text-[11px] uppercase tracking-wider text-ink-muted">
                  <tr>
                    <th className="px-4 py-3">Name</th>
                    {schema.slice(0, 3).map((f) => (
                      <th key={f.key} className="px-4 py-3">{f.label.split(" (")[0]}</th>
                    ))}
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {loading && <tr><td colSpan={4 + schema.length} className="px-4 py-10 text-center text-sm text-ink-muted">Loading…</td></tr>}
                  {!loading && items.length === 0 && (
                    <tr><td colSpan={4 + schema.length} className="px-4 py-12 text-center text-sm">
                      <Sparkles size={20} className="mx-auto mb-2 text-ink-muted" />
                      <div className="font-medium">No master {k.label.toLowerCase()} yet</div>
                      <div className="mt-1 text-xs text-ink-muted">Add one and it'll auto-seed into every new hospital at signup.</div>
                    </td></tr>
                  )}
                  {items.map((it) => (
                    <tr key={it.id} className="hover:bg-secondary/30" data-testid={`masters-row-${it.id}`}>
                      <td className="px-4 py-3 font-medium">{it.name}</td>
                      {schema.slice(0, 3).map((f) => (
                        <td key={f.key} className="px-4 py-3 text-xs text-ink-muted">{String(it[f.key] ?? "—").slice(0, 60)}</td>
                      ))}
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(it)} data-testid={`masters-edit-${it.id}`}><Pencil size={14} /></Button>
                          <Button size="sm" variant="ghost" onClick={() => remove(it)} data-testid={`masters-delete-${it.id}`}><Trash2 size={14} className="text-rose-500" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Per-hospital sync preview */}
      {preview && preview.hospitals.length > 0 && (
        <Card className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 size={14} className="text-brand" />
              <span className="text-sm font-semibold">Per-hospital sync status</span>
            </div>
            <Button size="sm" variant="ghost" onClick={loadPreview} data-testid="masters-refresh-preview"><RotateCw size={12} className="mr-1" /> Refresh</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="text-left text-[10px] uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className="py-2 pr-4">Hospital</th>
                  {KINDS.map((k) => <th key={k.key} className="px-2 py-2">{k.label.split(" ")[0]}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {preview.hospitals.map((h) => {
                  const total = Object.values(h.pending).reduce((a, b) => a + (b || 0), 0);
                  return (
                    <tr key={h.id}>
                      <td className="py-2 pr-4">
                        <div className="font-medium">{h.name}</div>
                        <div className="text-[10px] text-ink-muted">{h.code}</div>
                      </td>
                      {KINDS.map((k) => {
                        const n = h.pending[k.key] || 0;
                        return (
                          <td key={k.key} className="px-2 py-2 text-center tabular-nums">
                            {n === 0 ? <span className="text-leaf-700">✓</span> : <span className="font-semibold text-amber-700">{n}</span>}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-[11px] text-ink-muted">
            Numbers show pending items per kind. <b className="text-leaf-700">✓</b> means the hospital has every master item. Click <b>Sync to all hospitals</b> above to push pending items.
          </div>
        </Card>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {Tab && <Tab.icon size={16} className={Tab.color} />}
              {editing ? `Edit master ${Tab?.label.toLowerCase()}` : `Add to master ${Tab?.label.toLowerCase()}`}
            </DialogTitle>
            <DialogDescription className="text-[11px] text-ink-muted">
              {editing ? "Edits here only affect the master template. Hospitals keep their existing copies." : "This will auto-seed into every new hospital. Use 'Sync to all hospitals' afterwards to push to existing ones."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="masters-form-name" autoFocus />
            </div>
            {!editing && (
              <div>
                <Label>Seed key (optional — auto-generated)</Label>
                <Input value={form.master_seed_key} onChange={(e) => setForm({ ...form, master_seed_key: e.target.value })} placeholder="e.g. med-moxiflox-0.5" data-testid="masters-form-seedkey" />
                <div className="mt-1 text-[10px] text-ink-muted">Used to de-duplicate on re-sync. Stays unique per kind.</div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              {schema.map((f) => (
                <div key={f.key} className={f.type === "textarea" ? "col-span-2" : ""}>
                  <Label>{f.label}</Label>
                  {f.type === "textarea" ? (
                    <textarea
                      value={form.fields[f.key] || ""}
                      onChange={(e) => setField(f.key, e.target.value)}
                      className="mt-1.5 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      rows={3}
                      data-testid={`masters-form-${f.key}`}
                    />
                  ) : (
                    <Input
                      type={f.type === "number" ? "number" : "text"}
                      value={form.fields[f.key] ?? ""}
                      onChange={(e) => setField(f.key, f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
                      data-testid={`masters-form-${f.key}`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-brand hover:bg-brand-700" data-testid="masters-form-save">
                {editing ? "Save changes" : "Add to master"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
